import { Fragment, useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Pencil, Star, Trash2 } from "lucide-react";
import MyCropper from "../../tools/MyCropper";
import { setPreload } from "../../../server/CartsOpen";
import { updateProductFields } from "../../../server/Product";
import * as actionType from "../../../store/action";
import {
  deleteProductMedia,
  updateImgProduct,
  buildPimImagePayload,
} from "../../../server/UploadImage";
import showAlert, {
  showAlertWithConfirmCancel,
} from "../../../server/ShowAlert";
import {
  dedupeMediaByPath,
  formatMediaOrder,
  getDefaultMediaItem,
  isDefaultMedia,
  reindexMediaOrders,
  resolveMediaDefaultFlag,
} from "../../../utils/productMedia";
import {
  getProductImageUrl,
  canRecropProductImage,
} from "../../../utils/productImageLoader";
import { getProductHeaderMetaParts } from "../../../utils/formatProductPackSize";
import useFamilyProductUpdate, {
  applySequentially,
} from "../productPage/useFamilyProductUpdate";
import { formatImageSourceDisplay } from "../../../utils/productImageSource";
import CategoryPeerImages from "./CategoryPeerImages";
import useCategoryPeerProducts from "./useCategoryPeerProducts";

const toMediaPayload = (img, defaultOverride, index = 0) => {
  const isDefault = resolveMediaDefaultFlag(img, defaultOverride);
  return buildPimImagePayload({ ...img, default: isDefault }, index);
};

const normalizeMediaForApi = (images) => {
  const list = dedupeMediaByPath(images).map((img, index) =>
    toMediaPayload(img, undefined, index),
  );

  if (!list.length) return [];

  const hasDefault = list.some((img) => img.default === true);

  return list.map((img, index) => {
    const isDefault = hasDefault ? img.default === true : index === 0;
    return { ...img, default: isDefault, isDefault };
  });
};

const getMainImage = (images) => getDefaultMediaItem(images);

const ProductEditImg = ({
  product: productProp,
  isModal = false,
  onMediaSaved,
}) => {
  const selectedProduct = useSelector((state) => state.selectedProduct.product);
  const product =
    productProp &&
    productProp.productId != null &&
    String(selectedProduct?.productId) === String(productProp.productId)
      ? selectedProduct
      : productProp || selectedProduct;
  const stationId = useSelector((state) => state.user.stationId);
  const productCategories = useSelector(
    (state) => state.selectedProduct.productCategories,
  );
  const productHeaderMetaParts = getProductHeaderMetaParts(product);

  const [imagesList, setImagesList] = useState([]);
  const [recropTarget, setRecropTarget] = useState(null);
  const clearRecropTarget = useCallback(() => setRecropTarget(null), []);
  const dispatch = useDispatch();
  const { requestUpdate, familyDialog } = useFamilyProductUpdate();
  const {
    peers,
    categoryName,
    categorySlug,
    loading: peersLoading,
    loadFailed: peersLoadFailed,
  } = useCategoryPeerProducts({
    stationId,
    productId: product?.productId,
    productCategories:
      productCategories?.length > 0 ? productCategories : product?.categories,
  });

  useEffect(() => {
    if (product?.media?.length) {
      setImagesList(reindexMediaOrders(product.media));
    } else {
      setImagesList([]);
    }
  }, [product]);

  const validImages = useMemo(
    () => dedupeMediaByPath(imagesList),
    [imagesList],
  );

  const mainImage = useMemo(() => getMainImage(validImages), [validImages]);

  const getImageUrl = (img) => getProductImageUrl(img?.path);

  const buildImageApiPayload = (nextMedia) => normalizeMediaForApi(nextMedia);

  const revertImagesToProduct = useCallback(() => {
    if (product?.media?.length) {
      setImagesList(reindexMediaOrders(product.media));
    } else {
      setImagesList([]);
    }
  }, [product]);

  const persistImagesForProduct = useCallback(
    async (targetProductId, nextMedia) => {
      const normalized = buildImageApiPayload(nextMedia);
      const imagesPayload = normalized.map((img, index) => ({
        ...img,
        order: formatMediaOrder(img.order, index),
      }));

      await updateImgProduct({
        productId: targetProductId,
        images: imagesPayload,
      });

      await updateProductFields(stationId, targetProductId, {
        media: imagesPayload,
      });

      return normalized;
    },
    [stationId],
  );

  const applyLocalImageState = useCallback(
    (normalized) => {
      const mediaWithFlags = normalized.map((img) => ({
        ...img,
        isDefault: img.default === true,
      }));

      setImagesList(mediaWithFlags);
      dispatch({
        type: actionType.SET_SELCTED_PRODUCT,
        data: { Product: { ...product, media: mediaWithFlags } },
      });
      return mediaWithFlags;
    },
    [dispatch, product],
  );

  const commitImageChange = useCallback(
    (nextMedia, { deletePath, onError, onSuccess } = {}) => {
      requestUpdate(async (productsToChange) => {
        dispatch(setPreload(true));
        try {
          await applySequentially(productsToChange, async (prod) => {
            if (deletePath) {
              await deleteProductMedia({
                productId: prod.productId,
                branch: stationId,
                path: deletePath,
              });
            }
            await persistImagesForProduct(prod.productId, nextMedia);
          });

          const normalized = buildImageApiPayload(nextMedia);
          const mediaWithFlags = applyLocalImageState(normalized);
          onMediaSaved?.(productsToChange, mediaWithFlags);
          onSuccess?.();
        } catch (err) {
          onError?.(err);
          revertImagesToProduct();
        } finally {
          dispatch(setPreload(false));
        }
      }, revertImagesToProduct);
    },
    [
      applyLocalImageState,
      dispatch,
      onMediaSaved,
      persistImagesForProduct,
      requestUpdate,
      revertImagesToProduct,
      stationId,
    ],
  );

  const runImageDelete = (index) => {
    const imageToDelete = validImages[index];
    if (!imageToDelete?.path) return;

    const removedWasDefault = isDefaultMedia(imageToDelete);
    const remaining = [
      ...validImages.slice(0, index),
      ...validImages.slice(index + 1),
    ];
    const newMedia = remaining.map((img, i) =>
      toMediaPayload(
        img,
        removedWasDefault && remaining.length > 0 ? i === 0 : undefined,
        i,
      ),
    );

    commitImageChange(newMedia, {
      deletePath: imageToDelete.path,
      onSuccess: () => {
        showAlert({
          msgTitle: "התמונה נמחקה בהצלחה",
          msgType: "success",
        });
      },
      onError: (err) => {
        console.error(err);
        showAlert({
          msgTitle: "שגיאה",
          msgText: "מחיקת התמונה נכשלה",
          msgType: "error",
        });
      },
    });
  };

  const deleteImage = (index) => {
    const imageToDelete = validImages[index];
    if (!imageToDelete?.path) return;

    const isLastImage = validImages.length === 1;

    showAlertWithConfirmCancel({
      msgTitle: "האם אתה בטוח שברצונך למחוק את התמונה?",
      confirmText: "מחק",
      cancelText: "ביטול",
      onConfirm: () => {
        if (!isLastImage) {
          runImageDelete(index);
          return;
        }

        showAlertWithConfirmCancel({
          msgTitle:
            "זו התמונה היחידה שקיימת למוצר זה, האם בכ״א למחוק את התמונה האחרונה?",
          confirmText: "מחק",
          cancelText: "ביטול",
          onConfirm: () => runImageDelete(index),
        });
      },
    });
  };

  const setDefaultImage = (index) => {
    const targetPath = validImages[index]?.path;
    if (!targetPath || isDefaultMedia(validImages[index])) return;

    const newMedia = validImages.map((img, i) =>
      toMediaPayload(img, img.path === targetPath, i),
    );

    commitImageChange(newMedia, {
      onError: (err) => {
        console.error(err);
        const apiMessage =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          (typeof err?.response?.data === "string" ? err.response.data : null);
        showAlert({
          msgTitle: "שגיאה",
          msgText: apiMessage || "שמירת תמונה ראשית נכשלה",
          msgType: "error",
        });
      },
    });
  };

  const uploadImg = async (data) => {
    const uploaded = data?.newMedia || data?.media?.find((img) => img?.path);
    if (!uploaded?.path) return;

    const setAsDefault =
      data?.setAsDefault === true || uploaded.default === true;
    const newOrder = validImages.length;
    const newItem = toMediaPayload(uploaded, setAsDefault, newOrder);
    const existing = validImages.map((img, index) =>
      toMediaPayload(img, setAsDefault ? false : undefined, index),
    );
    const merged = [...existing, newItem];

    commitImageChange(merged, {
      onError: (err) => {
        console.error(err);
        const apiMessage =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          (typeof err?.response?.data === "string" ? err.response.data : null);
        showAlert({
          msgTitle: "שגיאה",
          msgText: apiMessage || "העלאת התמונה נכשלה",
          msgType: "error",
        });
      },
    });
  };

  if (!product) {
    if (!isModal) return null;
    return (
      <div className="product-edit-img product-edit-img--modal">
        <header className="product-edit-img-modal-header">
          <h2 className="product-edit-img-modal-header__title">עריכת תמונות</h2>
        </header>
        <div className="product-edit-img-modal-scroll">
          <p className="product-edit-img-modal-loading">טוען תמונות מוצר…</p>
        </div>
      </div>
    );
  }

  const productImagesPanel = (
    <>
      <div className="product-edit-img__upload-row">
        <MyCropper
          usePgwProductImage
          uploadImg={uploadImg}
          itemId={product.productId}
          recrop={
            recropTarget
              ? {
                  imageUrl: getImageUrl(recropTarget),
                  sourcePath: recropTarget.path,
                  source: recropTarget.source,
                }
              : null
          }
          onRecropClose={clearRecropTarget}
        />
      </div>

      {mainImage && (
        <div className="product-edit-img__hero">
          <img
            className="product-edit-img__hero-image"
            src={getImageUrl(mainImage)}
            alt="תמונה ראשית"
          />
        </div>
      )}

      {validImages.length ? (
        <div className="img-list product-edit-img-list">
          {validImages.map((img, index) => {
            const isDefault =
              isDefaultMedia(img) ||
              (!validImages.some(isDefaultMedia) && index === 0);

            return (
              <div
                key={`${img.path}-${index}`}
                className="product-edit-img-thumb-wrap"
              >
                <div
                  className={`product-edit-img-thumb${isDefault ? " product-edit-img-thumb--default" : ""}`}
                  style={{
                    position: "relative",
                    width: "100px",
                    borderRadius: "5px",
                    overflow: "hidden",
                  }}
                >
                  <div className="product-edit-img-thumb__image">
                    <img
                      src={getImageUrl(img)}
                      alt=""
                      style={{
                        width: "100px",
                        height: "100px",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </div>

                  <button
                    type="button"
                    className={`product-edit-img-thumb__star${isDefault ? " product-edit-img-thumb__star--active" : ""}`}
                    onClick={() => setDefaultImage(index)}
                    aria-label={isDefault ? "תמונה ראשית" : "הגדר כתמונה ראשית"}
                    title={isDefault ? "תמונה ראשית" : "הגדר כתמונה ראשית"}
                  >
                    <Star size={16} strokeWidth={2} aria-hidden />
                  </button>

                  {canRecropProductImage(img.path) ? (
                    <button
                      type="button"
                      className="product-edit-img-thumb__edit"
                      onClick={() => setRecropTarget(img)}
                      aria-label="ערוך תמונה"
                      title="חתוך מחדש"
                    >
                      <Pencil size={14} strokeWidth={2} aria-hidden />
                    </button>
                  ) : null}

                  <button
                    type="button"
                    className="product-edit-img-thumb__delete"
                    onClick={() => deleteImage(index)}
                    aria-label="מחק תמונה"
                  >
                    <Trash2 size={16} strokeWidth={2} aria-hidden />
                  </button>
                </div>
                <p className="product-edit-img-thumb__source">
                  <span className="product-edit-img-thumb__source-label">
                    מקור:
                  </span>{" "}
                  {formatImageSourceDisplay(img)}
                </p>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="product-edit-img__empty">אין תמונות להצגה</div>
      )}
    </>
  );

  const peerImagesPanel = (
    <CategoryPeerImages
      peers={peers}
      categoryName={categoryName}
      categorySlug={categorySlug}
      loading={peersLoading}
      loadFailed={peersLoadFailed}
    />
  );

  const editorBody = isModal ? (
    <div className="product-edit-img-modal-layout">
      <section className="product-edit-img-modal-main">
        {productImagesPanel}
      </section>
      <aside className="product-edit-img-modal-peers">{peerImagesPanel}</aside>
    </div>
  ) : (
    <>
      {productImagesPanel}
      {peerImagesPanel}
    </>
  );

  return (
    <Fragment>
      <div
        className={`product-edit-img${isModal ? " product-edit-img--modal" : ""}`}
      >
        {isModal ? (
          <>
            <header className="product-edit-img-modal-header">
              <h2 className="product-edit-img-modal-header__title">
                עריכת תמונות
              </h2>
              {productHeaderMetaParts.length ? (
                <p className="product-edit-img-modal-header__name">
                  {productHeaderMetaParts.join(" | ")}
                </p>
              ) : null}
              {product?.gtin ? (
                <p className="product-edit-img-modal-header__gtin">
                  ברקוד: {product.gtin}
                </p>
              ) : null}
            </header>
            <div className="product-edit-img-modal-scroll">{editorBody}</div>
          </>
        ) : (
          editorBody
        )}
      </div>
      {familyDialog}
    </Fragment>
  );
};

export default ProductEditImg;
